import { Link } from 'react-router-dom';
import { publicCatalogApi } from '@/features/publicsite/catalog/api/publicCatalogApi';
import { CatalogCard } from '@/features/publicsite/catalog/CatalogCard';
import { CatalogSection } from '@/features/publicsite/catalog/CatalogSection';
import { ResearchScientificEvents } from '@/features/publicsite/catalog/ResearchScientificEvents';
import { projectPath } from '@/features/publicsite/projectPath';
import { projectStatusLabel } from '@/features/publicsite/catalog/projectStatus';
import { useHashTarget } from '@/features/publicsite/catalog/useHashTarget';
import type { ResearchProject } from '@/features/publicsite/types/research/ResearchProject';
import { usePublicList } from '@/features/publicsite/usePublicList';
import { publicCatalog } from '@/i18n/dictionaries/publicCatalog';
import { useTranslation } from '@/i18n/useTranslation';

export function ResearchPage() {
  const { t } = useTranslation(publicCatalog);
  const projects = usePublicList<ResearchProject>(publicCatalogApi.researchProjectsUrl);
  // The home page's publications strip links to /cercetare#proiect-{id}; the anchors below only
  // exist once this list has arrived.
  useHashTarget(projects !== null);

  return (
    <CatalogSection
      eyebrow={t('eyebrowCatalog')}
      heading={t('researchHeading')}
      lead={t('researchLead')}
      // The homepage's catalogue teaser calls this whole area "organisations" and links here; the
      // page itself lists projects, so it hands the reader straight on to the profiles.
      headLink={
        <Link className="head-link" to="/cercetare/organizatii">
          {t('orgsLink')}
        </Link>
      }
      items={projects}
      renderCard={(p) => (
        <CatalogCard
          key={p.id}
          id={`proiect-${p.id}`}
          // The card is the way into the project's own page, where its published results are.
          // Until that page existed the count below was the end of the trail.
          to={projectPath(p)}
          badge={projectStatusLabel(t, p.status)}
          title={p.title}
          subtitle={p.organization}
        >
          <p>{p.summary}</p>
          <p className="muted">{t('researchResults', { count: p.resultCount })}</p>
        </CatalogCard>
      )}
      after={<ResearchScientificEvents />}
    />
  );
}
